/*
 * useScopes — Phase S-1 (Feb 2026)
 *
 * Resolves the current admin's scope_role + scope set from /admin/me/scopes
 * and exposes:
 *
 *   const { hasScope, scopeRole, isWildcard, loading } = useScopes();
 *   {hasScope("admin.invoice.refund") && <Button>Refund</Button>}
 *
 * Non-admin users return loading=false + isWildcard=false + hasScope→false.
 *
 * Cached per app lifecycle in a module singleton so multiple components
 * don't refetch. Cleared automatically when a new JWT lands in localStorage
 * (login/logout/impersonation switch).
 */
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";


let _cache = null;     // { admin_id, scope_role, scopes:Set, is_wildcard }
let _inflight = null;  // promise dedupe
let _lastToken = null;


async function _fetch() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const { data } = await api.get("/admin/me/scopes");
      _cache = {
        admin_id:    data.admin_id,
        scope_role:  data.scope_role,
        label:       data.label,
        color:       data.color,
        scopes:      new Set(data.scopes || []),
        is_wildcard: !!data.is_wildcard,
      };
      return _cache;
    } catch (e) {
      // Non-admin or 401 — return empty set so hasScope() returns false everywhere.
      _cache = {
        admin_id: null, scope_role: null, label: null, color: null,
        scopes: new Set(), is_wildcard: false,
      };
      return _cache;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}


export function clearScopesCache() {
  _cache = null;
  _inflight = null;
}


export function useScopes() {
  const { user } = useAuth();
  const [state, setState] = useState(_cache);
  const [loading, setLoading] = useState(_cache === null);

  useEffect(() => {
    // Re-fetch when the JWT changes (login/logout/impersonate).
    const token = localStorage.getItem("token");
    if (token !== _lastToken) {
      _lastToken = token;
      clearScopesCache();
    }
    // Only fetch for admin users; non-admins always get empty.
    if (!user || user.role !== "admin") {
      setState({
        admin_id: null, scope_role: null, label: null, color: null,
        scopes: new Set(), is_wildcard: false,
      });
      setLoading(false);
      return;
    }
    setLoading(true);
    _fetch().then((d) => {
      setState(d);
      setLoading(false);
    });
  }, [user]);

  const hasScope = (s) => {
    if (!state) return false;
    if (state.is_wildcard) return true;
    return state.scopes.has(s);
  };

  return {
    hasScope,
    scopeRole:  state?.scope_role || null,
    label:      state?.label || null,
    color:      state?.color || null,
    isWildcard: !!state?.is_wildcard,
    loading,
  };
}
